import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { InvalidationEntity } from "../entityDependencies";
import type { CacheSlot, Merge, RecordChange } from "./cacheSlots";

/** An entity's cache locations resolved for one organization. */
export interface BoundEntityCache<TId, TPatch, R = unknown> {
  readonly entity: InvalidationEntity;
  readonly orgId: string;
  readonly slots: readonly CacheSlot<TId, TPatch, R>[];
  /** Keys that belong to one record only (detail, documents): dropped when it is deleted. */
  records(id: TId): readonly QueryKey[];
  /** The record before and after a patch (`null` for a removal), read before any slot writes. */
  change(client: QueryClient, id: TId, patch: TPatch | null): RecordChange<R>;
}

export interface EntityCache<TId, TPatch, R = unknown> {
  readonly entity: InvalidationEntity;
  at(orgId: string): BoundEntityCache<TId, TPatch, R>;
}

/**
 * Declares, once per entity, every cache shape its records live in. Mutations
 * then patch, insert or remove records by id without knowing any query key.
 * `row` merges a patch into the list row the entity's views and aggregates read.
 */
export function defineEntityCache<TId, TPatch, R = unknown>(def: {
  entity: InvalidationEntity;
  row?: Merge<R, TPatch>;
  slots: (orgId: string) => readonly CacheSlot<TId, TPatch, R>[];
  records: (orgId: string, id: TId) => readonly QueryKey[];
}): EntityCache<TId, TPatch, R> {
  return {
    entity: def.entity,
    at: (orgId) => {
      const slots = def.slots(orgId);
      return {
        entity: def.entity,
        orgId,
        slots,
        records: (id) => def.records(orgId, id),
        change: (client, id, patch) => {
          const read = (prefix: QueryKey) => client.getQueriesData({ queryKey: prefix });
          let before: R | undefined;
          const holders: Readonly<Record<string, unknown>>[] = [];
          for (const slot of slots) {
            const entries = read(slot.prefix);
            before ??= slot.find?.(entries, id);
            holders.push(...(slot.holders?.(entries, id) ?? []));
          }
          const after =
            patch === null || before === undefined || !def.row ? undefined : def.row(before, patch);
          return { before, after, holders, read };
        },
      };
    },
  };
}
